// Loan Compliance Module v1.0 PR 7 + PR 9 — Track 3 per-engagement
// workbench host. The engagement QUEUE moved into the Compliance cockpit
// (/compliance, "Annual legal reviews (LLCR)" section —
// EngagementQueueSection); this route now only hosts the privileged
// per-engagement workbench at /compliance/legal-review/:reviewId. The
// list URL (/compliance/legal-review) redirects to the cockpit section,
// preserving its query params so old links and the loan-tab cross-link
// keep their loan pre-filter. Pool-scope engagements are hidden while
// PreContributionScope is on (no pools exist pre-contribution).

import { Navigate, useParams, useSearchParams } from "react-router-dom";
import { Card } from "@/design-system/components/ui";
import { useAuth } from "@/hooks/useAuth";
import { useFeatureFlag } from "@/hooks/portfolioAdmin/useFeatureFlag";
import type { LegalReviewScope } from "@/api/compliance/legalReviewTypes";
import { LegalReviewWorkbench } from "@/components/compliance/legalReview/LegalReviewWorkbench";

export default function LegalReviewWorkspacePage() {
  const auth = useAuth();
  const preContribution = useFeatureFlag("PreContributionScope");
  const { reviewId } = useParams<{ reviewId: string }>();
  const [params] = useSearchParams();

  if (!reviewId) {
    // The retired queue URL — land on the cockpit's attorney section with
    // the original params (?loanId= keeps pre-filtering the queue).
    const next = new URLSearchParams(params);
    next.set("section", "legal-reviews");
    return <Navigate to={`/compliance?${next.toString()}`} replace />;
  }

  const isPrivileged =
    auth.hasRoleCode("A3") ||
    auth.hasRoleCode("E4") ||
    auth.hasRoleCode("ADMIN");

  if (!isPrivileged) {
    return (
      <Card>
        <h2 className="font-display font-bold text-h2 m-0">Restricted</h2>
        <p className="text-small text-gray-mid mt-space-2 m-0">
          The Legal Review workbench is available to compliance attorneys (A3)
          and the chief compliance officer (E4).
        </p>
      </Card>
    );
  }

  const queryLoanId = params.get("loanId") ?? "";
  const queryPoolId = params.get("poolId") ?? "";
  const urlScope = (params.get("scope") ?? "").toLowerCase();

  // The workbench expects exactly one of loan / pool. Prefer explicit
  // ?scope=pool, otherwise infer from whichever id was passed.
  const scope: LegalReviewScope =
    urlScope === "pool" || (!urlScope && queryPoolId && !queryLoanId)
      ? "pool"
      : "loan";

  if (scope === "pool" && preContribution) {
    return <Navigate to="/compliance" replace />;
  }

  const subjectId = scope === "pool" ? queryPoolId : queryLoanId;
  return (
    <LegalReviewWorkbench
      reviewId={reviewId}
      scope={scope}
      subjectId={subjectId}
    />
  );
}
